import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, LinearProgress, Tabs, Tab, Table,
  TableHead, TableRow, TableCell, Paper, Divider,
  FormControlLabel, Checkbox,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import FavoriteIcon from '@mui/icons-material/Favorite';
import {
  MY_GIFT_REGISTRY_QUERY, MY_GIFTS_RECEIVED_QUERY, MY_GIFT_SUMMARY_QUERY, MY_CEREMONIES_QUERY,
  CREATE_GIFT_REGISTRY_ITEM_MUTATION, UPDATE_GIFT_REGISTRY_ITEM_MUTATION, DELETE_GIFT_REGISTRY_ITEM_MUTATION,
  RECORD_GIFT_RECEIVED_MUTATION, UPDATE_GIFT_RECEIVED_MUTATION, DELETE_GIFT_RECEIVED_MUTATION,
} from '../graphql/queries';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const PRIORITY_COLOR = { high:'error', medium:'warning', low:'default' };
const GIFT_TYPE_COLOR = { physical:'info', cash:'success', digital:'secondary', experience:'primary' };
const CATEGORIES = ['home','kitchen','electronics','experiences','travel','jewellery','fashion','other'];

const EMPTY_ITEM = { name:'', category:'home', description:'', preferredBrand:'', priceEstimate:'', productUrl:'', priority:'medium', quantityNeeded:1, notes:'' };
const EMPTY_GIFT = { fromGuestName:'', itemDescription:'', category:'home', valueEstimate:'', giftType:'physical', receivedAt:'', ceremonyId:'', notes:'' };

const fmt = n => n!=null ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

export default function GiftRegistryPage() {
  const [tab, setTab]   = useState(0);
  const [error, setError] = useState('');
  const [itemDialog, setItemDialog] = useState(false);
  const [editItem, setEditItem]     = useState(null);
  const [itemForm, setItemForm]     = useState(EMPTY_ITEM);
  const [giftDialog, setGiftDialog] = useState(false);
  const [editGift, setEditGift]     = useState(null);
  const [giftForm, setGiftForm]     = useState(EMPTY_GIFT);
  const [confirmDel, setConfirmDel] = useState(null);
  const [confirmDelGift, setConfirmDelGift] = useState(null);

  const { data: registryData, loading: rLoad, refetch: refetchRegistry } = useQuery(MY_GIFT_REGISTRY_QUERY, { fetchPolicy:'cache-and-network' });
  const { data: receivedData, loading: gLoad, refetch: refetchReceived }  = useQuery(MY_GIFTS_RECEIVED_QUERY, { fetchPolicy:'cache-and-network' });
  const { data: summaryData }  = useQuery(MY_GIFT_SUMMARY_QUERY, { fetchPolicy:'cache-and-network' });
  const { data: cerData }      = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy:'cache-and-network' });

  const registryItems = registryData?.myGiftRegistryItems || [];
  const giftsReceived = receivedData?.myGiftsReceived || [];
  const summary       = summaryData?.myGiftSummary || {};
  const ceremonies    = cerData?.myCeremonies || [];

  const [createItem]  = useMutation(CREATE_GIFT_REGISTRY_ITEM_MUTATION, { onCompleted:()=>{ setItemDialog(false); setItemForm(EMPTY_ITEM); setError(''); refetchRegistry(); }, onError:e=>setError(e.message) });
  const [updateItem]  = useMutation(UPDATE_GIFT_REGISTRY_ITEM_MUTATION, { onCompleted:()=>{ setItemDialog(false); setEditItem(null); setError(''); refetchRegistry(); }, onError:e=>setError(e.message) });
  const [deleteItem]  = useMutation(DELETE_GIFT_REGISTRY_ITEM_MUTATION, { onCompleted:()=>{ setConfirmDel(null); refetchRegistry(); } });
  const [recordGift]  = useMutation(RECORD_GIFT_RECEIVED_MUTATION, { onCompleted:()=>{ setGiftDialog(false); setGiftForm(EMPTY_GIFT); setError(''); refetchReceived(); refetchRegistry(); }, onError:e=>setError(e.message) });
  const [updateGift]  = useMutation(UPDATE_GIFT_RECEIVED_MUTATION, { onCompleted:()=>{ setGiftDialog(false); setEditGift(null); setError(''); refetchReceived(); }, onError:e=>setError(e.message) });
  const [deleteGift]  = useMutation(DELETE_GIFT_RECEIVED_MUTATION, { onCompleted:()=>{ setConfirmDelGift(null); refetchReceived(); } });

  const openEditItem = (item) => { setEditItem(item); setItemForm({ name:item.name||'', category:item.category||'home', description:item.description||'', preferredBrand:item.preferredBrand||'', priceEstimate:item.priceEstimate||'', productUrl:item.productUrl||'', priority:item.priority||'medium', quantityNeeded:item.quantityNeeded||1, notes:item.notes||'' }); setItemDialog(true); };
  const openEditGift = (g) => { setEditGift(g); setGiftForm({ fromGuestName:g.fromGuestName||'', itemDescription:g.itemDescription||'', category:g.category||'home', valueEstimate:g.valueEstimate||'', giftType:g.giftType||'physical', receivedAt:g.receivedAt||'', ceremonyId:g.ceremonyId||'', notes:g.notes||'' }); setGiftDialog(true); };
  const handleSaveItem = () => {
    const vars = { ...itemForm, priceEstimate:itemForm.priceEstimate?parseFloat(itemForm.priceEstimate):undefined, quantityNeeded:parseInt(itemForm.quantityNeeded)||1 };
    editItem ? updateItem({ variables:{ id:editItem.id, ...vars } }) : createItem({ variables:vars });
  };
  const handleSaveGift = () => {
    const vars = { ...giftForm, valueEstimate:giftForm.valueEstimate?parseFloat(giftForm.valueEstimate):undefined, ceremonyId: giftForm.ceremonyId || null };
    editGift ? updateGift({ variables:{ id:editGift.id, itemDescription:giftForm.itemDescription, valueEstimate:vars.valueEstimate, notes:giftForm.notes } }) : recordGift({ variables:vars });
  };

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Gift Registry</Typography>
          <Typography variant="body2" color="text.secondary">Manage your wedding wish list and track gifts received</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon/>} onClick={()=>tab===0?( setEditItem(null), setItemForm(EMPTY_ITEM), setItemDialog(true)):( setEditGift(null), setGiftForm(EMPTY_GIFT), setGiftDialog(true))}>
          {tab===0?'Add Registry Item':'Record Gift'}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={2} mb={3}>
        {[
          ['Registry Items', summary.totalItems||0, 'info'],
          ['Fulfilled',      summary.fulfilledItems||0, 'success'],
          ['Gifts Received', summary.totalGiftsReceived||0, 'primary'],
          ['Total Value',    fmt(summary.totalValue), 'warning'],
          ['Thank-You Pending', summary.thankYouPending||0, 'error'],
        ].map(([label,val,color])=>(
          <Grid item xs={6} sm={2.4} key={label}>
            <Card variant="outlined" sx={{textAlign:'center',p:1.5}}>
              <Typography variant="h5" fontWeight={700} color={`${color}.main`}>{val}</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{mb:2}}>
        <Tab label={`Gift Registry (${registryItems.length})`}/>
        <Tab label={`Gifts Received (${giftsReceived.length})`}/>
      </Tabs>

      {tab===0 && (
        <Grid container spacing={2}>
          {(rLoad) && <Grid item xs={12}><LinearProgress/></Grid>}
          {registryItems.length===0 && !rLoad && <Grid item xs={12}><Typography color="text.secondary" textAlign="center" mt={3}>No registry items yet. Click "Add Registry Item" to start building your wish list.</Typography></Grid>}
          <Paged items={registryItems}>{rows => rows.map(item=>(
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card variant="outlined" sx={{height:'100%',position:'relative'}}>
                {item.isFulfilled && <Chip label="Fulfilled" color="success" size="small" sx={{position:'absolute',top:8,right:8}}/>}
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <CardGiftcardIcon color="primary"/>
                    <Box flex={1}>
                      <Typography fontWeight={600}>{item.name}</Typography>
                      {item.preferredBrand && <Typography variant="caption" color="text.secondary">{item.preferredBrand}</Typography>}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" gap={0.5}>
                    <Chip size="small" label={item.category} variant="outlined"/>
                    <Chip size="small" label={item.priority} color={PRIORITY_COLOR[item.priority]||'default'}/>
                    <Typography variant="caption" sx={{ml:'auto',alignSelf:'center'}}>{fmt(item.priceEstimate)}</Typography>
                  </Stack>
                  {item.description && <Typography variant="body2" color="text.secondary" mt={1} sx={{WebkitLineClamp:2,display:'-webkit-box',overflow:'hidden',WebkitBoxOrient:'vertical'}}>{item.description}</Typography>}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
                    <Typography variant="caption">{item.quantityReceived||0}/{item.quantityNeeded} received</Typography>
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" onClick={()=>openEditItem(item)}><EditIcon fontSize="small"/></IconButton>
                      <IconButton size="small" color="error" onClick={()=>setConfirmDel(item.id)}><DeleteIcon fontSize="small"/></IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}</Paged>
        </Grid>
      )}

      {tab===1 && (
        <Box>
          {(gLoad) && <LinearProgress sx={{mb:2}}/>}
          {giftsReceived.length===0 && !gLoad && <Typography color="text.secondary" textAlign="center" mt={3}>No gifts recorded yet.</Typography>}
          <Table>
            <TableHead><TableRow>
              <TableCell>From</TableCell><TableCell>Item</TableCell><TableCell>Type</TableCell>
              <TableCell>Value</TableCell><TableCell>Thank-You</TableCell><TableCell/>
            </TableRow></TableHead>
            <TableBody>
              {giftsReceived.map(g=>(
                <TableRow key={g.id}>
                  <TableCell><Typography fontWeight={500}>{g.fromGuestName}</Typography></TableCell>
                  <TableCell>{g.itemDescription}</TableCell>
                  <TableCell><Chip size="small" label={g.giftType} color={GIFT_TYPE_COLOR[g.giftType]||'default'}/></TableCell>
                  <TableCell>{fmt(g.valueEstimate)}</TableCell>
                  <TableCell>
                    {g.thankyouSent
                      ? <Chip size="small" icon={<CheckCircleIcon/>} label="Sent" color="success"/>
                      : <Button size="small" variant="outlined" color="warning"
                          onClick={()=>updateGift({variables:{id:g.id,thankyouSent:true,thankyouMessage:`Dear ${g.fromGuestName}, thank you so much for the wonderful gift! It means a lot to us.`}})}>
                          Send Thank-You
                        </Button>
                    }
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={()=>openEditGift(g)}><EditIcon fontSize="small"/></IconButton>
                    <IconButton size="small" color="error" onClick={()=>setConfirmDelGift(g.id)}><DeleteIcon fontSize="small"/></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Registry Item Dialog */}
      <Dialog open={itemDialog} onClose={()=>setItemDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editItem?'Edit Registry Item':'Add Registry Item'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField label="Item Name *" value={itemForm.name} onChange={e=>setItemForm(f=>({...f,name:e.target.value}))} fullWidth/>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField select label="Category" value={itemForm.category} onChange={e=>setItemForm(f=>({...f,category:e.target.value}))} fullWidth>{CATEGORIES.map(c=><MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></Grid>
            <Grid item xs={6}><TextField select label="Priority" value={itemForm.priority} onChange={e=>setItemForm(f=>({...f,priority:e.target.value}))} fullWidth>{['high','medium','low'].map(p=><MenuItem key={p} value={p}>{p}</MenuItem>)}</TextField></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Preferred Brand" value={itemForm.preferredBrand} onChange={e=>setItemForm(f=>({...f,preferredBrand:e.target.value}))} fullWidth/></Grid>
            <Grid item xs={6}><TextField label="Price Estimate (₹)" type="number" value={itemForm.priceEstimate} onChange={e=>setItemForm(f=>({...f,priceEstimate:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <TextField label="Product URL / Link" value={itemForm.productUrl} onChange={e=>setItemForm(f=>({...f,productUrl:e.target.value}))} fullWidth/>
          <TextField label="Qty Needed" type="number" value={itemForm.quantityNeeded} onChange={e=>setItemForm(f=>({...f,quantityNeeded:e.target.value}))} fullWidth/>
          <TextField label="Description" value={itemForm.description} onChange={e=>setItemForm(f=>({...f,description:e.target.value}))} multiline rows={2} fullWidth/>
          <TextField label="Notes" value={itemForm.notes} onChange={e=>setItemForm(f=>({...f,notes:e.target.value}))} multiline rows={2} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setItemDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveItem} disabled={!itemForm.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Record Gift Dialog */}
      <Dialog open={giftDialog} onClose={()=>setGiftDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editGift?'Edit Gift':'Record Gift Received'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField label="From (Guest Name) *" value={giftForm.fromGuestName} onChange={e=>setGiftForm(f=>({...f,fromGuestName:e.target.value}))} fullWidth/>
          <TextField label="Item Description *" value={giftForm.itemDescription} onChange={e=>setGiftForm(f=>({...f,itemDescription:e.target.value}))} fullWidth/>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField select label="Gift Type" value={giftForm.giftType} onChange={e=>setGiftForm(f=>({...f,giftType:e.target.value}))} fullWidth>{['physical','cash','digital','experience'].map(t=><MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField></Grid>
            <Grid item xs={6}><TextField label="Estimated Value (₹)" type="number" value={giftForm.valueEstimate} onChange={e=>setGiftForm(f=>({...f,valueEstimate:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField select label="Category" value={giftForm.category} onChange={e=>setGiftForm(f=>({...f,category:e.target.value}))} fullWidth>{CATEGORIES.map(c=><MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></Grid>
            <Grid item xs={6}><TextField select label="Ceremony" value={giftForm.ceremonyId} onChange={e=>setGiftForm(f=>({...f,ceremonyId:e.target.value}))} fullWidth><MenuItem value="">— None —</MenuItem>{ceremonies.map(c=><MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}</TextField></Grid>
          </Grid>
          <TextField label="Received On" type="date" value={giftForm.receivedAt} onChange={e=>setGiftForm(f=>({...f,receivedAt:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/>
          <TextField label="Notes" value={giftForm.notes} onChange={e=>setGiftForm(f=>({...f,notes:e.target.value}))} multiline rows={2} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setGiftDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveGift} disabled={!giftForm.fromGuestName||!giftForm.itemDescription}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Deletes */}
      <Dialog open={!!confirmDel} onClose={()=>setConfirmDel(null)}>
        <DialogTitle>Delete Registry Item?</DialogTitle>
        <DialogActions>
          <Button onClick={()=>setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deleteItem({variables:{id:confirmDel}})}>Delete</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!confirmDelGift} onClose={()=>setConfirmDelGift(null)}>
        <DialogTitle>Delete Gift Record?</DialogTitle>
        <DialogActions>
          <Button onClick={()=>setConfirmDelGift(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deleteGift({variables:{id:confirmDelGift}})}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
